function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styled, { ThemeConsumer } from 'styled-components';
import { borderRadius } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { Box, borderRadiusAttrs, deprecatedPropType, ThemeProvider } from 'pcln-design-system';
import FocusLock from 'react-focus-lock';
import PopoverArrow from '../Arrow';
import Overlay from '../Overlay';
const ESCAPE_KEY = 27;
const PopoverGuide = styled(Box).withConfig({
  displayName: "PopoverContent__PopoverGuide",
  componentId: "sc-1olhkof-0"
})(["box-sizing:border-box;max-width:", ";width:100%;z-index:", ";position:relative;"], _ref => {
  let {
    width
  } = _ref;
  return typeof width === 'number' ? `${width}px` : width;
}, _ref2 => {
  let {
    zIndex
  } = _ref2;
  return zIndex < 0 ? 1 : zIndex;
});
const ContentContainer = styled.section.attrs(borderRadiusAttrs).withConfig({
  displayName: "PopoverContent__ContentContainer",
  componentId: "sc-1olhkof-1"
})(["box-sizing:border-box;font-size:", "px;max-width:100%;outline:0;", ";"], themeGet('fontSizes.0'), borderRadius);
function PopoverContent(_ref3) {
  let {
    arrowRef,
    borderColor,
    borderRadius,
    className,
    color,
    idx,
    onCloseRequest,
    overlayOpacity,
    placement,
    renderContent,
    trapFocus,
    hideArrow,
    hideOverlay,
    popoverRef,
    styles,
    width,
    zIndex,
    ...props
  } = _ref3;
  const handleKeyUp = useCallback(evt => {
    if (evt.keyCode === ESCAPE_KEY) {
      evt.stopPropagation();
      evt.preventDefault();
      onCloseRequest(evt);
    }
  }, [onCloseRequest]);
  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp, false);
    return () => {
      window.removeEventListener('keyup', handleKeyUp, false);
    };
  }, [handleKeyUp]);
  const getBorderColorName = (color, borderColor) => {
    let borderColorName = borderColor;
    if (!borderColor) {
      if (color) {
        borderColorName = color;
      } else {
        borderColorName = 'border';
      }
    }
    return borderColorName;
  };
  const styleProps = {
    borderColor: getBorderColorName(color, borderColor),
    borderRadius,
    width,
    zIndex
  };
  const content = trapFocus ? /*#__PURE__*/React.createElement(FocusLock, {
    focusOptions: {
      preventScroll: true
    }
  }, renderContent({
    handleClose: onCloseRequest
  })) : renderContent({
    handleClose: onCloseRequest
  });
  return /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PopoverGuide, _extends({
    "aria-describedby": `dialog-description-${idx}`,
    className: className,
    "data-placement": placement,
    ref: popoverRef,
    role: "dialog",
    style: styles?.popover
  }, styleProps, props), /*#__PURE__*/React.createElement(ThemeConsumer, null, theme => /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(ContentContainer, _extends({}, styleProps, {
    tabIndex: "-1",
    "data-testid": "popover-content-container"
  }), /*#__PURE__*/React.createElement(Box, {
    boxShadowSize: "overlay-md",
    borderRadius: borderRadius,
    color: color ?? 'background.lightest',
    "data-testid": "dialog-content",
    id: `popover-description-${idx}`,
    tabIndex: "-1"
  }, content)))), !hideArrow && /*#__PURE__*/React.createElement(PopoverArrow, {
    borderColor: styleProps.borderColor,
    color: color ?? 'background.lightest',
    placement: placement,
    ref: arrowRef,
    style: styles?.arrow
  })), !hideOverlay && /*#__PURE__*/React.createElement(Overlay, {
    handleClick: onCloseRequest,
    overlayOpacity: overlayOpacity,
    zIndex: zIndex - 1
  })),
  // Append each instance of the Popover as portal directly to the body
  document.querySelector('body'));
}
PopoverContent.propTypes = {
  ...borderRadius.propTypes,
  idx: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  // ID needs to be required for A11y purposes. We need to uniquely identify each popover on screen
  renderContent: PropTypes.func.isRequired,
  onCloseRequest: PropTypes.func.isRequired,
  contentRef: PropTypes.object,
  className: PropTypes.string,
  p: PropTypes.number,
  color: PropTypes.string,
  bg: deprecatedPropType('color'),
  borderColor: PropTypes.string,
  placement: PropTypes.string,
  zIndex: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  overlayOpacity: PropTypes.number,
  trapFocus: PropTypes.bool,
  hideArrow: PropTypes.bool,
  hideOverlay: PropTypes.bool
};
PopoverContent.defaultProps = {
  p: 2,
  placement: 'top',
  zIndex: 102,
  width: 400
};
export default PopoverContent;